import { useGetContextsQuery } from "Auth/Api/Queries/Context/useGetContextsQuery";
import { RevalidateBankingAuthModal } from "Components/Banking/Auth/RevalidateBankingAuthModal";
import { BetaInfoDrawer } from "Components/Beta/BetaInfoDrawer";
import { DailyAppOpenTracker } from "Components/Loyalty/DailyAppOpenTracker";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { useHunterLicenseGate } from "Hooks/useHunterLicenseGate";
import { useIsCurrentUserHunter } from "Hooks/useIsCurrentUserHunter";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { IdentityUserStatus, UserStatus } from "Infrastructure/Api/Api";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import { Navigate } from "Navigation/Navigate";
import { resetUser, setUnauthenticatedUrl } from "State/Auth/AuthReducer";
import type { RootStateType } from "State/Store";
import { appUrl } from "Utils/UrlUtils";
import { type ReactNode, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { type RouteProps, useLocation } from "react-router";

type Props = RouteProps & {
  isUserAuthenticated: boolean;
  allowAnonymous?: boolean;
  /** A page outside the app proper, e.g. the consent screen of a partner sign-in. */
  isStandalone?: boolean;
  children: ReactNode;
};

export const AuthenticatedRoute = ({
  children,
  isUserAuthenticated,
  allowAnonymous,
  isStandalone,
}: Props) => {
  const wasPreviouslyAuthenticatedRef = useRef(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const auth = useSelector((state: RootStateType) => state.auth);
  const user = useLoggedInUser();
  const isHunter = useIsCurrentUserHunter();

  // Enabled only when authenticated — hooks run before the redirect below, and an unauthenticated
  // 401 here would trip the global sign-out handling for nothing.
  const { data: contextsData, isLoading: areContextsLoading } =
    useGetContextsQuery(isUserAuthenticated);
  const activeContext = contextsData?.contexts?.find(
    (context) =>
      context.identityUserPublicID === contextsData.activeIdentityUserPublicID,
  );
  const isOnboardingBusinessContext =
    activeContext?.status === IdentityUserStatus.Onboarding &&
    (activeContext.accessRoleCode === AccessRoleCodes.Hunter ||
      activeContext.accessRoleCode === AccessRoleCodes.HunterPlus ||
      activeContext.accessRoleCode === AccessRoleCodes.Merchant);

  // Self-disables for non-hunters (the licenses query is skipped), so only hunters
  // pay for the extra request — and only on a cold cache.
  const { state: licenseGateState } = useHunterLicenseGate();

  useEffect(() => {
    if (!isUserAuthenticated && location.pathname !== "/") {
      dispatch(resetUser({}));
      const shouldSaveUrl = !wasPreviouslyAuthenticatedRef.current; // Only save URL if user was NOT previously authenticated (initial visit, not logout)

      if (shouldSaveUrl) {
        dispatch(setUnauthenticatedUrl(location.pathname + location.search));
      }
    }
    wasPreviouslyAuthenticatedRef.current = isUserAuthenticated;
  }, [dispatch, isUserAuthenticated, location.pathname, location.search]);

  // Contexts must be loaded before rendering children: the add-role gate below can't decide yet,
  // and a prematurely mounted dashboard consumes the bank-callback query param the wizard needs
  // to resume on its bank step. isLoading stays false while the query is disabled (signed out).
  if (
    auth.isUserFetching ||
    areContextsLoading ||
    licenseGateState === "loading"
  ) {
    return <CenteredCircularProgress color="grey" />;
  }

  if (!isUserAuthenticated) {
    return (
      <Navigate to={appUrl("sign-in")} state={{ from: location }} replace />
    );
  }

  if (!allowAnonymous && user?.userStatus === UserStatus.Anonymous) {
    return (
      <Navigate to={appUrl("dashboard")} state={{ from: location }} replace />
    );
  }

  if (user?.migrationRequired) {
    if (location.pathname !== appUrl("migration-required")) {
      return <Navigate to={appUrl("migration-required")} replace />;
    }

    // The migration page owns a not-yet-linked account. The Identity-era gates below have nothing
    // to decide for it, and the licence gate would bounce a licence-less legacy hunter between this
    // page and the blocked page forever.
    return (
      <>
        {children}
        <BetaInfoDrawer />
      </>
    );
  }

  if (user?.userStatus === UserStatus.Onboarding) {
    return <Navigate to={appUrl("onboarding")} replace />;
  }

  if (user?.userStatus === UserStatus.RoleSelection) {
    return <Navigate to={appUrl("complete-sso-registration")} replace />;
  }

  // The wizard and licence redirects below would drop a partner's sign-in request, and app-wide
  // dialogs or a daily open don't belong on a page the person only passes through.
  if (isStandalone) {
    return <>{children}</>;
  }

  // Mirror of the legacy UserStatus.Onboarding gate for the Identity model: a self-added business
  // role stays in its wizard until completed — the rest of the app isn't usable in that context
  // yet (the user can still switch back to another role via the context switcher). The query
  // string must survive (bank OAuth callbacks land on /dashboard with params the wizard reads).
  if (
    isOnboardingBusinessContext &&
    location.pathname !== appUrl("add-role-onboarding")
  ) {
    return (
      <Navigate
        to={`${appUrl("add-role-onboarding")}${location.search}`}
        replace
      />
    );
  }

  // Hunters without any active license are locked out of the app until their license
  // payment is confirmed (or until they order one). Onboarding contexts are exempt —
  // a wizard hunter has no license yet (or an unpaid one on the payment step), and on
  // the wizard's own path the redirect above doesn't fire, so without the exemption
  // this gate would bounce them to the blocked page and the two Navigates would loop.
  // The blocked page is fully self-contained (ordering, cancelling, payment
  // details), so nothing else is reachable while blocked.
  const isLicenseBlocked =
    licenseGateState === "paymentPending" || licenseGateState === "noLicense";
  if (
    isLicenseBlocked &&
    !isOnboardingBusinessContext &&
    location.pathname !== appUrl("license-payment-pending")
  ) {
    return <Navigate to={appUrl("license-payment-pending")} replace />;
  }

  // Three states fall through to here without being in the app proper: the license-blocked
  // hunter and the add-role wizard on their own landing pages (the redirects above skip those
  // paths), and an anonymous user on an allowAnonymous route. None of them counts as a daily open.
  const isDailyAppOpenCounted =
    !isLicenseBlocked &&
    !isOnboardingBusinessContext &&
    user?.userStatus !== UserStatus.Anonymous;

  return (
    <>
      {children}
      {!isHunter && !user?.migrationRequired && <RevalidateBankingAuthModal />}
      <BetaInfoDrawer />
      {isDailyAppOpenCounted && <DailyAppOpenTracker />}
    </>
  );
};
